// Sim event delivery on the client (§20.10). Server events about the local bean dispatch on arrival;
// the rest wait for the render timeline. Local predicted events from fresh prediction steps
// dispatch at once on 'sim:local', and the matching server copy is then suppressed. Time-derived
// events (stripped by the server) come from fresh prediction steps only. Every server event is
// dispatched exactly once or suppressed by exactly one predicted event. DOM-free.
import { NO_SLOT } from '@shared/constants';
import { EV, type SimEvent } from '@shared/events';
import type { Bus } from '../core/bus';

/** Server events dispatched on arrival when `a` or `b` is the local slot. */
export const ABOUT_ME_EVENTS: readonly number[] = [
  EV.HIT, EV.GRAB, EV.THROW, EV.ESCAPE, EV.FALL, EV.KO, EV.STOMP, EV.PEEL_SLIP, EV.PLUNGER_HIT,
  EV.PEBBLE_HIT, EV.POTATO_PASS, EV.POTATO_NEW, EV.BALLOON_SAVE, EV.CROWN_POP,
];
/** Events with `a == localSlot` that fresh prediction steps dispatch on 'sim:local'. */
export const LOCAL_PREDICTED_EVENTS: readonly number[] = [
  EV.LUNGE, EV.JUMP, EV.LAND, EV.TEETER, EV.TEETER_SAVE, EV.LEDGE, EV.CLIMB, EV.ITEM_USE,
  EV.PUNCH_WINDUP, EV.FLOAT_START, EV.HONK, EV.EMOTE, EV.PEEL_SLIP,
];
/** Stripped by the server; fresh prediction steps dispatch them on 'sim'. */
export const TIME_DERIVED_EVENTS: readonly number[] = [EV.PHASE, EV.COUNTDOWN, EV.HAZARD_WARN, EV.HAZARD_FIRE];

export const EVENT_SCHEDULER = {
  /** A predicted event suppresses a server event of the same type and slot within ±30 ticks. */
  matchWindowTicks: 30,
  /** Predicted dispatches older than this (behind the newest fresh tick) can no longer match. */
  predictedMemoryTicks: 240,
  /** Dispatched events kept for debugging (`__SHOVEOFF__.events`). */
  recentSize: 256,
  /** Counters are indexed by event type. */
  maxEventType: 256,
} as const;

/** Per-type counters for the event audit (index = event type). */
export interface EventStats {
  /** Server events received in snapshots. */
  received: Uint32Array;
  /** Server events dispatched on 'sim' (on arrival or on the render timeline). */
  dispatched: Uint32Array;
  /** Server events suppressed because a matching predicted event was already dispatched. */
  suppressed: Uint32Array;
  /** Local predicted events dispatched on 'sim:local'. */
  predicted: Uint32Array;
  /** Time-derived events dispatched from fresh prediction steps. */
  derived: Uint32Array;
}

interface PredictedDispatch { type: number; roomTick: number; c: number; matched: boolean }

function counters(): Uint32Array {
  return new Uint32Array(EVENT_SCHEDULER.maxEventType);
}

export class EventScheduler {
  readonly stats: EventStats = { received: counters(), dispatched: counters(), suppressed: counters(), predicted: counters(), derived: counters() };
  private localSlot = NO_SLOT;
  private readonly queue: SimEvent[] = [];
  private readonly queueTicks: number[] = [];
  private head = 0;
  private readonly predictedLog: PredictedDispatch[] = [];
  private lastFreshTick = -Infinity;
  private readonly recentEvents: SimEvent[] = [];
  private recentNext = 0;

  constructor(private readonly bus: Bus) {}

  /** Server events waiting for the render timeline. */
  get pending(): number {
    return this.queue.length - this.head;
  }

  setLocalSlot(slot: number): void {
    this.localSlot = slot;
  }

  /**
   * Server events of one snapshot, in order. `roundToRoom` converts their round ticks to room
   * ticks (the snapshot's `roomTick − roundTick`).
   */
  onServerEvents(events: readonly SimEvent[], roundToRoom: number): void {
    const local = this.localSlot;
    for (const ev of events) {
      this.stats.received[ev.type]++;
      const roomTick = ev.tick + roundToRoom;
      if (local !== NO_SLOT && ev.a === local && LOCAL_PREDICTED_EVENTS.includes(ev.type) && this.matchPredicted(ev, roomTick)) {
        this.stats.suppressed[ev.type]++;
        continue;
      }
      if (local !== NO_SLOT && (ev.a === local || ev.b === local) && ABOUT_ME_EVENTS.includes(ev.type)) {
        this.dispatchServer(ev);
        continue;
      }
      this.queue.push(ev);
      this.queueTicks.push(roomTick);
    }
  }

  /**
   * Events of one prediction step at room tick `roomTick`. Only fresh ticks count: a tick at or
   * below the newest one already seen (re-stepped after a hard resync) is ignored and false is
   * returned. Local events dispatch only while the local bean is predicted (`predictLocal`);
   * time-derived events always do.
   */
  onPredictedEvents(events: readonly SimEvent[], roomTick: number, predictLocal: boolean): boolean {
    if (roomTick <= this.lastFreshTick) return false;
    this.lastFreshTick = roomTick;
    this.forgetPredicted(roomTick - EVENT_SCHEDULER.predictedMemoryTicks);
    for (const ev of events) {
      if (TIME_DERIVED_EVENTS.includes(ev.type)) {
        this.stats.derived[ev.type]++;
        this.emit(ev, false);
      } else if (predictLocal && ev.a === this.localSlot && this.localSlot !== NO_SLOT && LOCAL_PREDICTED_EVENTS.includes(ev.type)) {
        this.stats.predicted[ev.type]++;
        this.predictedLog.push({ type: ev.type, roomTick, c: ev.c, matched: false });
        this.emit(ev, true);
      }
    }
    return true;
  }

  /** Dispatches queued server events whose room tick is at or before `renderTick`. */
  update(renderTick: number): void {
    while (this.head < this.queue.length && this.queueTicks[this.head] <= renderTick) this.dispatchServer(this.queue[this.head++]);
    this.compact();
  }

  /** Dispatches every queued event now. */
  flush(): void {
    while (this.head < this.queue.length) this.dispatchServer(this.queue[this.head++]);
    this.compact();
  }

  /**
   * A new world: flushes the old round's queue (nothing is ever dropped) and restarts the fresh
   * tick and the predicted-dispatch log, because the new round's opening ticks overlap the room
   * ticks the old round was already predicted to.
   */
  startRound(): void {
    this.flush();
    this.predictedLog.length = 0;
    this.lastFreshTick = -Infinity;
  }

  /** Forgets all state for a new room. Queued events are dropped. */
  reset(): void {
    this.queue.length = this.queueTicks.length = 0;
    this.head = 0;
    this.predictedLog.length = 0;
    this.lastFreshTick = -Infinity;
  }

  /** The last `n` dispatched events, oldest first. */
  recent(n: number = EVENT_SCHEDULER.recentSize): SimEvent[] {
    const size = this.recentEvents.length;
    const count = Math.max(0, Math.min(n, size));
    const out: SimEvent[] = [];
    for (let i = size - count; i < size; i++) out.push(this.recentEvents[(this.recentNext + i) % size]);
    return out;
  }

  /**
   * Consumes the oldest unmatched predicted dispatch of the same type within the window. A
   * server TEETER_SAVE with the CLUTCH flag still dispatches if the predicted one lacked it.
   */
  private matchPredicted(ev: SimEvent, roomTick: number): boolean {
    for (const p of this.predictedLog) {
      if (p.matched || p.type !== ev.type || Math.abs(p.roomTick - roomTick) > EVENT_SCHEDULER.matchWindowTicks) continue;
      p.matched = true;
      return !(ev.type === EV.TEETER_SAVE && ev.c === 1 && p.c !== 1);
    }
    return false;
  }

  private forgetPredicted(beforeTick: number): void {
    let k = 0;
    while (k < this.predictedLog.length && this.predictedLog[k].roomTick < beforeTick) k++;
    if (k > 0) this.predictedLog.splice(0, k);
  }

  private dispatchServer(ev: SimEvent): void {
    this.stats.dispatched[ev.type]++;
    this.emit(ev, false);
  }

  private emit(ev: SimEvent, local: boolean): void {
    if (this.recentEvents.length < EVENT_SCHEDULER.recentSize) this.recentEvents.push(ev);
    else this.recentEvents[this.recentNext] = ev;
    this.recentNext = (this.recentNext + 1) % EVENT_SCHEDULER.recentSize;
    if (local) this.bus.emit('sim:local', { ev, local: true });
    else this.bus.emit('sim', { ev, local: false });
  }

  private compact(): void {
    if (this.head === 0) return;
    if (this.head === this.queue.length) {
      this.queue.length = this.queueTicks.length = 0;
      this.head = 0;
    } else if (this.head >= 64) {
      this.queue.splice(0, this.head);
      this.queueTicks.splice(0, this.head);
      this.head = 0;
    }
  }
}
